// src/ProductGroups.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
const emptyGroup = { ProductGroup: '', ParentProductGroupKey: null, SortOrder: 0 };

function getItemTypeLabel(item) {
  if (!item) return '—';
  const candidates = [
    item.ItemTypeName,
    item.ItemType,
    item.ItemTypeLabel,
    item.ItemTypeDescription,
    item.DisplayName,
    item.label,
    item.name
  ];
  const label = candidates.find(value => typeof value === 'string' && value.trim().length);
  if (label) return label.trim();
  if (item.Code && typeof item.Code === 'string' && item.Code.trim()) return item.Code.trim();
  return item.ItemTypeKey ? `Item Type #${item.ItemTypeKey}` : 'Unnamed Item Type';
}

function getFitmentLocationLabel(entry) {
  if (!entry) return '—';
  const candidates = [
    entry.FitmentLocation,
    entry.Location,
    entry.location,
    entry.name,
    entry.OneOffGroup,
    entry.DisplayName,
    entry.Label
  ];
  const label = candidates.find(value => typeof value === 'string' && value.trim().length);
  if (label) return label.trim();
  if (entry.FitmentLocationKey) return `Location #${entry.FitmentLocationKey}`;
  if (entry.OneOffGroupKey) return `One-Off #${entry.OneOffGroupKey}`;
  return 'Unnamed Location';
}

export default function ProductGroups({ permissions, theme }) { // Receive permissions prop from App (for write checks) and theme prop (existing styling)
  // ─── State ───────────────────────────────────────────────
  const [groups, setGroups]                   = useState([]);
  const rootGroups = groups.filter(g => g.ParentProductGroupKey == null);
  const [newGroup, setNewGroup]               = useState(emptyGroup);

  // fetch all FitmentLocations so we can look up their ItemTypeKey
  const [allFitLocations, setAllFitLocations] = useState([]);

  // One-Off overlay state
  const [subGroups, setSubGroups]             = useState([]);
  const [selectedSubGroup, setSelectedSubGroup] = useState(null);
  const [oneOffs, setOneOffs]                 = useState([]);

  // Sub-Group creation state
  const [newSubName, setNewSubName]           = useState('');
  const [newSubSort, setNewSubSort]           = useState(0);

  // Link Management State
  const [itemTypes, setItemTypes]             = useState([]);
  const [linkedTypes, setLinkedTypes]         = useState([]);
  const [selectedGroup, setSelectedGroup]     = useState(null);
  const [stats,        setStats]        = useState(null);
  const [newLinkType, setNewLinkType] = useState("");

  // FitmentLocation management
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [fitLocations, setFitLocations]         = useState([]);
  const [newLocName, setNewLocName]             = useState('');
  const [newLocSort, setNewLocSort]             = useState(0);
  //ProductGroup custom where
  const [customWhere, setCustomWhere] = useState('');
  //ProductGroup load itemtypes
  const [availableItemTypes, setAvailableItemTypes] = useState([]); // State for available ItemTypes not yet linked

  // ─── Effects ─────────────────────────────────────────────
// 1️⃣ Fetch all groups
useEffect(() => {
  axios.get('/api/product-groups')
    .then(r => {
      console.log('Fetched groups:', r.data); // Log: Check response in browser console (empty? error?)
      setGroups(r.data);
    })
    .catch(err => {
      console.error('Failed to fetch product groups:', err); // Log: If fetch fails, see here
    });
}, []);


  // 4️⃣ When a group is selected, load its sub-groups (and clear downstream state)
  useEffect(() => {
    if (!selectedGroup) return;
    // clear downstream selections when group changes
    setSubGroups([]);
    setSelectedSubGroup(null);
    setSelectedItemType(null);    // ← NEW: drop any lingering ItemType
    setFitLocations([]);
    setLinkedTypes([]);
    setOneOffs([]);
    axios
      .get(`/api/product-groups/${selectedGroup.ProductGroupKey}/sub-groups`)
      .then(r => setSubGroups(r.data))
      .catch(console.error);
  }, [selectedGroup]);

  // 2️⃣ Fetch all ItemTypes for dropdown
  useEffect(() => {
    axios.get('/api/item-types')
      .then(r => setItemTypes(r.data))
      .catch(console.error);
  }, []);

  // 3️⃣ Fetch all FitmentLocations (so we can resolve ItemTypeKey → name)
  useEffect(() => {
    axios.get('/api/fitment-locations')
      .then(r => setAllFitLocations(r.data))
      .catch(console.error);
  }, []);


  // ─── Fetch stats when either a ProductGroup or Sub-Group is selected ─────
  useEffect(() => {
    if (selectedItemType) {
      // stats for the specific item-type within the sub-group
      axios
        .get(
         `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}` +
          `/item-types/${selectedItemType.ItemTypeKey}/statistics`
        )
        .then(r => setStats(r.data))
        .catch(console.error);
   } else if (selectedSubGroup) {
      // stats for the sub-group itself
      axios
        .get(
          `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/statistics`
        )
        .then(r => setStats(r.data))
        .catch(console.error);
    } else {
      setStats(null);
    }
  }, [selectedSubGroup, selectedItemType]);

  // 5️⃣ When a sub-group is selected, load its one-offs
  useEffect(() => {
    if (!selectedSubGroup) {
      setOneOffs([]);
      return;
    }
    axios.get(
      `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/one-off-groups`
    )
    .then(r => setOneOffs(r.data))
    .catch(console.error);
  }, [selectedSubGroup]);

  // 6️⃣ When group or subGroup changes, load the ItemType links
  useEffect(() => {
    const pgKey = selectedSubGroup
      ? selectedSubGroup.SubProductGroupKey
      : selectedGroup?.ProductGroupKey;
    if (!pgKey) return;
    axios.get(`/api/product-groups/${pgKey}/item-types`)
      .then(r => setLinkedTypes(r.data))
      .catch(console.error);
    axios.get(`/api/product-groups/${selectedGroup.ProductGroupKey}`)
  .then(r => setCustomWhere(r.data.CustomWhereClause || ''))
  .catch(console.error);
  }, [selectedGroup, selectedSubGroup]);

  // ─── Load custom WHERE for selected sub-group ───
useEffect(() => {
  if (!selectedSubGroup) {
    setCustomWhere('');
    return;
  }
  axios.get(`/api/product-groups/${selectedSubGroup.SubProductGroupKey}`)
    .then(r => setCustomWhere(r.data.CustomWhereClause || ''))
    .catch(console.error);
}, [selectedSubGroup]);

  // 7️⃣ When an ItemType is clicked, load its FitmentLocations
  useEffect(() => {
    if (!selectedItemType) return;
    axios.get(`/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`)
      .then(r => setFitLocations(r.data))
      .catch(console.error);
  }, [selectedItemType]);

// ─── Compute availableItemTypes from full itemTypes minus linkedTypes ───
useEffect(() => {
  if (itemTypes.length > 0 && linkedTypes.length >= 0) {
    const updatedAvailable = itemTypes.filter(it => 
      !linkedTypes.some(linked => linked.ItemTypeKey === it.ItemTypeKey)  // Exclude linked items from full list
    );
    setAvailableItemTypes(updatedAvailable);
    console.log('Computed availableItemTypes:', updatedAvailable); // Debug computed list
  }
}, [itemTypes, linkedTypes]); // Trigger on changes to full or linked lists


  // ─── Handlers ────────────────────────────────────────────

  // Create a new top-level ProductGroup
  function handleCreate(e) {
    e.preventDefault();
    axios.post('/api/product-groups', newGroup)
      .then(() => {
        setNewGroup(emptyGroup);
        return axios.get('/api/product-groups');
      })
      .then(r => setGroups(r.data))
      .catch(console.error);
  }

  // Delete a ProductGroup (with count of sub-groups)
  function handleGroupDelete(group) {
    axios.get(`/api/product-groups/${group.ProductGroupKey}/sub-groups`)
      .then(r => {
        const cnt = r.data.length;
        if (!window.confirm(
          `Delete Product Group “${group.ProductGroup}”? It has ${cnt} sub-group${cnt!==1?'s':''}.`
        )) return;
        return axios.delete(`/api/product-groups/${group.ProductGroupKey}`);
      })
      .then(() => axios.get('/api/product-groups'))
      .then(r => {
        setGroups(r.data);
        if (selectedGroup?.ProductGroupKey === group.ProductGroupKey) {
          setSelectedGroup(null);
        }
      })
      .catch(console.error);
  }

// Create a SubProductGroup
function handleAddSubGroup() {
  if (!selectedGroup) {
    return alert('❗️ Please select a Product Group first.');
  }
  if (!newSubName.trim()) {
    return alert('❗️ Enter a sub-group name.');
  }
  axios.post(`/api/product-groups/${selectedGroup.ProductGroupKey}/sub-groups`, {
    name: newSubName,
    SortOrder: newSubSort
  })
    .then(() =>
      axios.get(`/api/product-groups/${selectedGroup.ProductGroupKey}/sub-groups`)
    )
    .then(r => {
      setSubGroups(r.data);
      setNewSubName('');
      setNewSubSort(0);
      return axios.get('/api/product-groups');
    })
    .then(r => setGroups(r.data))
    .catch(console.error);
}

  // Delete a Sub-Group (with count of linked ItemTypes)
  function handleSubGroupDelete(sub) {
    const cnt = sub.ItemTypeCount;
    if (!window.confirm(
      `Delete sub-group “${sub.name}”? It has ${cnt} linked item type${cnt!==1?'s':''}.`
    )) return;
    axios.delete(`/api/product-groups/${sub.SubProductGroupKey}`)
      .then(() =>
        axios.get(`/api/product-groups/${selectedGroup.ProductGroupKey}/sub-groups`)
      )
      .then(r => {
        setSubGroups(r.data);
        if (selectedSubGroup?.SubProductGroupKey === sub.SubProductGroupKey) {
          setSelectedSubGroup(null);
          setLinkedTypes([]);
          setFitLocations([]);
          setOneOffs([]);
        }
      })
      .catch(console.error);
  }


// ─── Add a new ItemType↔ProductGroup link ───────────────
const handleLinkAdd = (itemTypeKey) => {
  if (!selectedGroup || selectedGroup === null || selectedGroup === undefined || !selectedGroup.ProductGroupKey) {
    console.error('selectedGroup is invalid:', selectedGroup);
    alert('No ProductGroup selected. Please select a group before linking an ItemType.');
    return;
  }
  if (!selectedSubGroup || !selectedSubGroup.SubProductGroupKey) {
    console.error('selectedSubGroup is invalid:', selectedSubGroup);
    alert('No Sub-Group selected. Please select a sub-group before linking an ItemType.');
    return;
  }
  axios.post('/api/item-type-product-groups', { 
    ProductGroupKey: selectedSubGroup.SubProductGroupKey,  // Use sub-group's ProductGroupKey for linking
    ItemTypeKey: itemTypeKey 
  })
    .then(response => {
      console.log('POST response:', response.data); // Debug POST success
      // Fetch updated linked item types for the selected sub-group
      return axios.get(`/api/product-groups/${selectedSubGroup.SubProductGroupKey}/item-types`)
        .then(linkedRes => {
          const newLinkedTypes = linkedRes.data;
          setLinkedTypes(newLinkedTypes); // Update linkedTypes with fresh data (triggers useEffect for available)
          console.log('Fetched linked ItemTypes:', newLinkedTypes); // Debug fetched data
        })
        .catch(err => {
          console.error('Failed to refresh linked item types:', err);
          alert('ItemType linked, but failed to refresh list. Please refresh the page.');
          // Fallback: Re-fetch initial linked item types for sub-group
          axios.get(`/api/product-groups/${selectedSubGroup.SubProductGroupKey}/item-types`)
            .then(fallbackRes => {
              setLinkedTypes(fallbackRes.data); // Fallback update for linkedTypes
              console.log('Fallback linked ItemTypes:', fallbackRes.data); // Debug fallback
            })
            .catch(fallbackErr => console.error('Fallback failed:', fallbackErr));
        });
    })
    .catch(error => {
      console.error('Error linking ItemType:', error);
      alert(`Failed to link ItemType: ${error.response?.data?.error || 'Server error'}`);
    });
};


// ─── Remove an ItemType↔ProductGroup link (with cascade-delete of overrides) ───────
async function handleLinkRemove(itemTypeKey) {
  // 1️⃣ Determine which PG/SPG this is under
  const pgKey = selectedSubGroup
    ? selectedSubGroup.SubProductGroupKey
    : selectedGroup?.ProductGroupKey;

  // 2️⃣ Find overrides tied to this ItemType
  const toDelete = oneOffs.filter(oo => {
    const fl = allFitLocations.find(f => f.FitmentLocationKey === oo.FitmentLocationKey);
    return fl?.ItemTypeKey === itemTypeKey;
  });

  // 3️⃣ If any exist, confirm and delete them first
  if (toDelete.length > 0) {
    const plural = toDelete.length > 1 ? 's' : '';
    if (!window.confirm(`Removing this ItemType will also remove ${toDelete.length} one-off override${plural}. Continue?`)) {
      return;
    }
    // Delete all overrides in parallel (continue even if fails)
    await Promise.all(
      toDelete.map(oo =>
        axios.delete(`/api/one-off-groups/${oo.OneOffGroupKey}`)
          .catch(err => console.error('Failed to delete override:', err))  // Log error but continue
      )
    );
    // Refresh the oneOffs list
    const r2 = await axios.get(`/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/one-off-groups`)
      .catch(err => {
        console.error('Failed to refresh oneOffs:', err);
        alert('Failed to refresh overrides after delete: Server error');
        return { data: oneOffs }; // Fallback to current state
      });
    setOneOffs(r2.data);
  }

  // 4️⃣ Remove the ItemType ↔ ProductGroup link and refresh linked types
  try {
    await axios.delete('/api/item-type-product-groups', { data: { ProductGroupKey: pgKey, ItemTypeKey: itemTypeKey } });  // Use pgKey for the delete call
    const updatedRes = await axios.get(`/api/product-groups/${pgKey}/item-types`);
    setLinkedTypes(updatedRes.data); // Update linkedTypes with fresh data (triggers useEffect for available)
    console.log('Updated linked ItemTypes after remove:', updatedRes.data); // Debug updated data
  } catch (err) {
    console.error('Error removing ItemType link:', err);
    alert(`Failed to remove ItemType: ${err.response?.data?.error || 'Server error'}`);
  }
}

  // Add / Delete a FitmentLocation
  function handleAddLocation() {
    if (!selectedGroup) {
      return alert('❗️ Please click a Product Group first.');
    }
    if (!selectedItemType) {
      return alert('❗️ Please select an Item Type first.');
    }
    axios.post('/api/fitment-locations', {
      Location:        newLocName,
      SortOrder:       newLocSort,
      ItemTypeKey:     selectedItemType.ItemTypeKey,
      ProductGroupKey: selectedGroup.ProductGroupKey
    })
    .then(() =>
      axios.get(`/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`)
    )
    .then(r => {
      setFitLocations(r.data);
      setNewLocName('');
      setNewLocSort(0);
    })
    .catch(console.error);
  }
  function handleDeleteLocation(key) {
    axios.delete(`/api/fitment-locations/${key}`)
      .then(() =>
        axios.get(`/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`)
      )
      .then(r => setFitLocations(r.data))
      .catch(console.error);
  }

  // ─── Render ────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ─── Column 1: Product Groups ───────────────────────── */}
      <aside style={{
        width: '280px', padding: '1rem',
        borderRight: '1px solid #ccc', overflowY: 'auto'
      }}>
        {/* Create new ProductGroup */}
        <form onSubmit={handleCreate} style={{ marginBottom: '1rem' }}>
          <input
            placeholder="Name"
            value={newGroup.ProductGroup}
            onChange={e =>
              setNewGroup({ ...newGroup, ProductGroup: e.target.value })
            }
          />
          <input
            type="number"
            placeholder="Sort"
            value={newGroup.SortOrder}
            onChange={e =>
              setNewGroup({ ...newGroup, SortOrder: Number(e.target.value) })
            }
            style={{ width: '4ch', marginLeft: '0.5rem' }}
          />
          {permissions['OEM References & Item Types'] === 'write' && (
          <button type="submit" style={{ marginLeft: '0.5rem' }}>
            Create
          </button>
          )}
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {rootGroups.map(g => (
            <li key={g.ProductGroupKey} style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '0.5rem',
              background: selectedGroup?.ProductGroupKey === g.ProductGroupKey ? '#eef' : 'transparent',
              cursor: 'pointer'
            }}>
              <span onClick={() => setSelectedGroup(g)}>
                {g.ProductGroup} (sort #{g.SortOrder})
              </span>
              {permissions['OEM References & Item Types'] === 'write' && (
              <button onClick={() => handleGroupDelete(g)}>
                Delete
              </button>)}
            </li>
          ))}
        </ul>
      </aside>

{/* ─── Column 2: Sub-Groups ───────────────────────────── */}
<aside style={{
  width: '300px', padding: '1rem',
  borderRight: '1px solid #ccc', overflowY: 'auto'
}}>
  {selectedGroup ? (
    <>
      <h3>
        Sub-Groups for 2“{selectedGroup.ProductGroup}”
        (Key: {selectedGroup.ProductGroupKey})
      </h3>

      {/* Create Sub-Group */}
      <div style={{ margin: '1rem 0', display: 'flex', gap: '0.5rem' }}>
        <input
          placeholder="New Sub-Group"
          value={newSubName}
          onChange={e => setNewSubName(e.target.value)}
        />
        <input
          type="number"
          placeholder="Sort"
          value={newSubSort}
          onChange={e => setNewSubSort(Number(e.target.value))}
          style={{ width: '4ch' }}
        />
        {permissions['OEM References & Item Types'] === 'write' && (
        <button onClick={handleAddSubGroup}>
          Create Sub-Group
        </button>)}
      </div>

      {/* List Sub-Groups with delete/button */}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {subGroups.map(s => (
          <li key={s.SubProductGroupKey} style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', padding: '0.5rem',
            background:
              selectedSubGroup?.SubProductGroupKey === s.SubProductGroupKey
                ? '#eef'
                : 'transparent',
            cursor: 'pointer'
          }}>
            <span
              onClick={() => {
                setSelectedSubGroup(s);
                setSelectedItemType(null);
                setOneOffs([]);
              }}
            >
              {s.name}
            </span>
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              {s.ItemTypeCount} type{s.ItemTypeCount!==1?'s':''}
              {permissions['OEM References & Item Types'] === 'write' && (
              <button
                style={{ marginLeft: '0.5rem' }}
                onClick={() => handleSubGroupDelete(s)}
              >
                Delete
              </button>)}
            </div>
          </li>
        ))}
       </ul>

      {/* Custom WHERE Clause Input Block for Selected Sub-Group */}
      {selectedSubGroup && (
        <div style={{ marginTop: '1rem' }}>
          <label>Custom WHERE Clause for “{selectedSubGroup.name}”:</label>
          <textarea
            value={customWhere}
            onChange={e => setCustomWhere(e.target.value)}
            style={{ width: '100%', height: '60px', padding: '0.5rem' }}
            placeholder="e.g., Models.VehicleType IN ('Scooter','Motorcycle')"
          />{permissions['OEM References & Item Types'] === 'write' && (
          <button 
            onClick={async () => {
              const clause = customWhere.trim();
              // Existing validation (unchanged)
              const invalidPatterns = [/;/, /--/, /EXEC/i, /DROP/i, /INSERT/i, /UPDATE/i, /DELETE/i, /xp_cmdshell/i, /UNION/i, /SELECT/i, /SUBQUERY/i, /CHAR\s*\(/i, /CONCAT/i, /0x[0-9A-F]+/i, /CAST/i, /@@/];
              if (invalidPatterns.some(p => p.test(clause))) {
                alert('Invalid WHERE clause: Contains disallowed SQL (e.g., ;, --, UNION, SELECT, CHAR). For safety, only simple conditions like comparisons/IN allowed.');
                return;
              }
              if (clause && !/^[a-zA-Z0-9_.,()=<>! '"]+(?:\s*(AND|OR|IN|NOT|LIKE)\s*[a-zA-Z0-9_.,()=<>! '"]+)*$/.test(clause)) {
                alert('Invalid WHERE clause: Only basic columns, operators (=, <, >, IN, AND/OR), and values allowed. No functions or complex syntax.');
                return;
              }
              try {
                // Loading feedback
                console.log('Saving clause:', clause);
                await axios.patch(`/api/product-groups/${selectedSubGroup.SubProductGroupKey}/custom-where`, { CustomWhereClause: clause });
                console.log('Save success');
                alert('Clause saved successfully!');
                // Refetch to confirm persistence
                const res = await axios.get(`/api/product-groups/${selectedSubGroup.SubProductGroupKey}`);
                setCustomWhere(res.data.CustomWhereClause || '');
              } catch (err) {
                console.error('Save failed:', err);
                alert(`Failed to save WHERE clause: ${err.message || 'Unknown error'}—check console.`);
              }
            }}
            style={{ marginTop: '0.5rem' }}
          >
            Save Clause
          </button>)}
        </div>
      )}
      
    </>
  ) : (
    <p style={{ fontStyle: 'italic' }}>Select a Product Group</p>
  )}
</aside>

      {/* ─── Column 3: ItemTypes & Fitment ───────────────────── */}
      <main style={{
        flex: 1, padding: '1rem',
        overflowY: 'auto', display: 'flex', flexDirection: 'column'
      }}>

      {stats && (
        <section style={{ margin: '1.5rem 0', padding: '1rem', border: '1px solid #ddd' }}>
          <h4>
            📊 Statistics for “
            {selectedItemType
              ? getItemTypeLabel(selectedItemType)
              : selectedSubGroup?.name}
            ”
          </h4>
          <ul style={{ listStyle: 'none', paddingLeft: 0, lineHeight: '1.6' }}>
            <li>Models total: {stats.modelCount}</li>
            <li>Base Fitment Locations: {stats.locationCount}</li>
            <li>Existing primary fitments: {stats.existingPrimary}</li>
            <li>Existing one-off fitments: {stats.oneOffExisting}</li>
            <li>
              <strong>Total possible fitments:</strong> {stats.totalPossible}
            </li>
            <li>
              <strong>Missing fitments to collect:</strong> {stats.neededCount}
            </li>
            <li>Misc (TBA/None) count: {stats.miscCount}</li>
          </ul>
        </section>
      )}

        {/* ItemTypes panel */}
        {selectedSubGroup ? (
          <section style={{ marginBottom: '2rem' }}>
            <h3>Item Types for “{selectedSubGroup?.name}”</h3>
            <ul key={linkedTypes.length} style={{ listStyle: 'none', padding: 0 }}> 
              {linkedTypes.map(lt => (
                <li key={lt.ItemTypeKey} style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '0.5rem'
                }}>
                  <span
                    onClick={() => setSelectedItemType(lt)}
                    style={{
                      cursor: 'pointer',
                      fontWeight:
                        selectedItemType?.ItemTypeKey === lt.ItemTypeKey
                          ? 'bold'
                          : 'normal'
                    }}
                  >
                    {getItemTypeLabel(lt)}
                  </span>
                  <button onClick={() => handleLinkRemove(lt.ItemTypeKey)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: '0.5rem' }}>
            <select
  value={newLinkType}
  onChange={e => {
    const key = Number(e.target.value);
   handleLinkAdd(key);
   }}
>
<option value="" disabled>
  ➕ Add Item Type…
</option>
  {availableItemTypes.map(it => (  // Use availableItemTypes from useEffect for full unlinked list
    <option key={it.ItemTypeKey} value={it.ItemTypeKey}>
      {getItemTypeLabel(it)}
    </option>
  ))}
</select>
            </div>
          </section>
        ) : (
          <p style={{ fontStyle: 'italic' }}>Select a Sub-Group</p>
        )}

        {/* Fitment + One-Off panel */}
        {selectedItemType && (
          <section>
            <h3>Fitment Locations for “{selectedItemType.ItemTypeName}”</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {fitLocations.map(fl => (
                <li key={fl.FitmentLocationKey} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span>
                      {getFitmentLocationLabel(fl)} (sort #{fl.SortOrder})
                    </span>
                    <div>
                      <button
                        onClick={() => handleDeleteLocation(fl.FitmentLocationKey)}
                        style={{ marginRight: '0.5rem' }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Promote to one-off?')) return;
                          const label = getFitmentLocationLabel(fl);
                          await axios.post(
                            `/api/fitment-locations/${fl.FitmentLocationKey}/promote`,
                            {
                              OneOffGroup:        label,
                              SubProductGroupKey: selectedSubGroup.SubProductGroupKey,
                              SortOrder:          fl.SortOrder,
                              ItemTypeKey:        selectedItemType?.ItemTypeKey ?? fl.ItemTypeKey ?? null
                            }
                          );
                          const [oneOffRes, fitRes] = await Promise.all([
                            axios.get(
                              `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/one-off-groups`
                            ),
                            axios.get(
                              `/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`
                            )
                          ]);
                          setOneOffs(oneOffRes.data);
                          setFitLocations(fitRes.data);
                        }}
                      >
                        Promote
                      </button>
                    </div>
                  </div>
                  {/* nested One-Offs under each FL */}
                  {oneOffs
                    .filter(oo => oo.FitmentLocationKey === fl.FitmentLocationKey)
                    .map(oo => (
                      <div key={oo.OneOffGroupKey} style={{
                        marginLeft: '1.5rem', color: '#a00',
                        display: 'flex', justifyContent: 'space-between'
                      }}>
                        <span>
                          One-Off: {getFitmentLocationLabel(oo)} (sort #{oo.SortOrder})
                        </span>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Demote this one-off?')) return;
                            await axios.delete(`/api/one-off-groups/${oo.OneOffGroupKey}`);
                            const [oneOffRes, fitRes] = await Promise.all([
                              axios.get(
                                `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/one-off-groups`
                              ),
                              axios.get(
                                `/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`
                              )
                            ]);
                            setOneOffs(oneOffRes.data);
                            setFitLocations(fitRes.data);
                          }}
                        >
                          Demote
                        </button>
                      </div>
                    ))}
                </li>
              ))}
            </ul>

            {/* Add new FitmentLocation */}
            <div style={{
              marginTop: '1rem', display: 'flex', gap: '0.5rem'
            }}>
              <input
                placeholder="New Location"
                value={newLocName}
                onChange={e => setNewLocName(e.target.value)}
              />
              <input
                type="number"
                placeholder="Sort"
                value={newLocSort}
                onChange={e => setNewLocSort(Number(e.target.value))}
                style={{ width: '4ch' }}
              />
              <button onClick={handleAddLocation}>
                Add Location
              </button>
            </div>
          </section>
        )}

        {/* ─── All One-Off Overrides ─────────────────────────── */}
        {selectedSubGroup && oneOffs.length > 0 && (
          <section style={{
            marginTop: '2rem', border: '1px solid #ccc', padding: '1rem'
          }}>
            <h4>
              All One-Off Overrides for “{selectedSubGroup?.name}”
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {oneOffs.map(oo => {
                const fl = allFitLocations.find(f => f.FitmentLocationKey === oo.FitmentLocationKey);
                const linkedItemType = itemTypes.find(i => i.ItemTypeKey === fl?.ItemTypeKey) || null;
                const it = getItemTypeLabel(linkedItemType);
                return (
                  <li key={oo.OneOffGroupKey} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '0.25rem 0', borderBottom: '1px solid #eee'
                  }}>
                    <span>
                      {getFitmentLocationLabel(oo)} (ItemType: {it}, FL #{oo.FitmentLocationKey}, sort #{oo.SortOrder})
                    </span>
                    <button onClick={async () => {
                      if (!window.confirm('Demote this one-off?')) return;
                      await axios.delete(`/api/one-off-groups/${oo.OneOffGroupKey}`);
                      // reload one-offs
                      const r = await axios.get(
                        `/api/sub-product-groups/${selectedSubGroup.SubProductGroupKey}/one-off-groups`
                      );
                      setOneOffs(r.data);
                      // reload FLs
                      const locs = await axios.get(
                        `/api/item-types/${selectedItemType.ItemTypeKey}/fitment-locations`
                      );
                      setFitLocations(locs.data);
                    }}>
                      Demote
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
